import React, { useEffect, useState } from 'react';
import { useParams, RouteComponentProps } from '@reach/router';
import Input from '../../components/Input';
import Button from '../../components/Button';
import { TrashCan } from '../../components/Icons';
import Table from '../../components/Table';
import { domain } from '../../d-man';
import { CostCode, useCreateCostCode, useGetCostCodes } from '../../queries/CostCodes';
import { useGetTrade } from '../../queries/Trades';
import Pager from '../../components/Pager';
import { useCurrentProjectId } from '../../queries/Accounts';
import { useGetPermissions } from '../../queries/Permissions';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function TradeDetail(props: RouteComponentProps) {
    const params = useParams();
    const getId = () => params.id;

    const PAGE_SIZE = 30;
    const [skip, setSkip] = useState(0);
    const costCodes = useGetCostCodes(skip, PAGE_SIZE, getId());

    const newCostCode = useCreateCostCode();
    const trade = useGetTrade(getId());

    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [name, setName] = useState('');
    const [code, setCode] = useState('');

    const projectId = useCurrentProjectId();
    const getPermissions = useGetPermissions('PROJECT', projectId.data);
    const { navigate } = props;
    // REDIRECT IF NOT CORRECT ROLE
    if (
        getPermissions.data &&
        !getPermissions.data?.items.find((p) => p.code === 'Project_Allow_Admin')?.hasAccess &&
        navigate
    ) {
        navigate(`/`);
    }

    useEffect(() => {
        if (newCostCode.error) {
            setErrorMessage(newCostCode.error || 'Failed to create new Cost Code');
        }
    }, [newCostCode.error]);

    const post = async () => {
        setIsLoading(true);
        setErrorMessage('');
        if (!name || !code) {
            setErrorMessage('Please capture all the fields above');
        } else {
            const data = {
                name,
                code,
                tradeId: trade.data?.id,
            };
            await newCostCode.execute(data);
            costCodes.reset();
        }
        setIsLoading(false);
    };

    const clearFields = () => {
        setErrorMessage('');
        setName('');
        setCode('');
    };

    /**
     * Does an HTTP Delete to the server for the specified id
     * @param id
     */
    const deleteCostCode = async (id?: string) => {
        const remove = domain.delete(`/costcodes/${id}`);
        await remove.execute();
        costCodes.reset();
    };

    const handleNextPage = () => {
        setSkip(skip + PAGE_SIZE);
        costCodes.reset();
    };

    const handlePreviousPage = () => {
        setSkip(Math.max(skip - PAGE_SIZE, 0));
        costCodes.reset();
    };

    return (
        <div className="flex flex-col w-full">
            {trade.data && (
                <div className="bg-surface border border-border rounded-xl m-4">
                    <div className="flex items-center justify-between gap-4 p-4 border-b border-border">
                        <div className="text-lg font-semibold text-foreground">
                            Trade Information
                        </div>
                    </div>
                    <div className="p-4 space-y-3">
                        <div className="flex items-center justify-between gap-3">
                            <div className="text-sm font-medium text-label">Code</div>
                            <div className="w-48">
                                <Input value={trade.data.code} type="text" disabled />
                            </div>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                            <div className="text-sm font-medium text-label">Description</div>
                            <div className="w-48">
                                <Input value={trade.data.description} type="text" disabled />
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <div className="flex flex-wrap items-end justify-between gap-4 bg-surface border border-border rounded-xl m-4 p-4">
                <div className="flex flex-wrap items-end gap-4">
                    <div className="text-sm font-semibold text-foreground pb-2 pr-4 border-r border-border">
                        New Cost Code
                    </div>
                    <div className="w-48">
                        <Input
                            label="Code"
                            value={code}
                            type="text"
                            onChange={(event) => setCode(event.target.value)}
                        />
                    </div>
                    <div className="w-48">
                        <Input
                            label="Name"
                            value={name}
                            type="text"
                            onChange={(event) => setName(event.target.value)}
                        />
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {errorMessage && <div className="text-sm text-danger">{errorMessage}</div>}
                    <Button secondary onClick={clearFields}>
                        Clear
                    </Button>
                    <Button onClick={post}>{!isLoading ? 'Submit' : <div>Loading</div>}</Button>
                </div>
            </div>
            <div className="m-4">
                <Table<CostCode>
                    loading={costCodes.loading && 'Fetching Cost Codes from server'}
                    error={
                        costCodes.error &&
                        `Failed to fetch Cost Codes list from the server (${costCodes.error})`
                    }
                    data={costCodes.data?.items}
                    columns={[
                        {
                            key: 'code',
                            title: 'Cost Code',
                        },
                        {
                            key: 'name',
                            title: 'Name',
                        },
                        {
                            key: 'actions',
                            title: '',
                            render: (costCode) => (
                                <TrashCan
                                    className="cursor-pointer flex"
                                    id={costCode.id}
                                    onClick={() => deleteCostCode(costCode.id)}
                                />
                            ),
                        },
                    ]}
                />
                {costCodes.data && costCodes.data.totalCount > PAGE_SIZE && (
                    <Pager
                        onNextPage={handleNextPage}
                        onPreviousPage={handlePreviousPage}
                        skip={skip}
                        pageSize={PAGE_SIZE}
                        objectList={costCodes.data}
                    />
                )}
            </div>
        </div>
    );
}
